import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DashboardPageLayout } from '@/components/layouts/dashboard-page-layout'
import { TabContainer } from '@/components/organisms/accounts-receivable-manager/tab-container'
import { css } from '@emotion/core'
import { AccommodationPaging } from '@/components/molecules/accommodation-paging'
import { exportAccountsReceivableForManager, fetchAccountsReceivableForManager, fetchSalesAccountsReceivable } from '@/apis/aipass'
import {
  AccountsReceivable,
  AccountsReceivableSearchCondition,
  FetchAccountsReceivableResponse,
  SortKeyType,
  SortOrderType,
} from '@/models/accounts-receivable-manager'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { useScrollToTop } from '@/hooks/use-scroll-to-top'
import { LoaderContextCreator } from '@/contexts/loader'
import { Button } from '@/components/atoms'
import { ButtonType } from '@/components/atoms/button'
import dayjs from 'dayjs'
import { Checkbox } from '@/components/atoms/checkbox'
import { Hint } from '@/components/atoms/hint'
import { ReceiptReconciliationModal } from '@/components/organisms/accounts-receivable-manager/recepit-reconciliation-modal'
import { AccountsReceivableFilterPopup } from '@/components/organisms/accounts-receivable-manager/accounts-receivable-filter-popup'
import { Popover } from 'antd'
import uuid from 'react-uuid'
import { useHistory } from 'react-router-dom'
import { TypeLabel } from '@/components/organisms/accounts-receivable-manager/type-label'

const PER_PAGE_NUMBER = 50

const defaultSearchCondition: AccountsReceivableSearchCondition = {
  page: 1,
  checkinFrom: undefined,
  checkinTo: undefined,
  checkoutFrom: dayjs()
    .subtract(1, 'month')
    .startOf('month')
    .format('YYYY-MM-DD'),
  checkoutTo: dayjs()
    .subtract(1, 'month')
    .endOf('month')
    .format('YYYY-MM-DD'),
  customerName: undefined,
  salesType: undefined,
  reconciledStatus: 'unreconciled',
  sortKey: SortKeyType.CheckinDate,
  sortOrder: SortOrderType.Asc,
}

export const AccountsReceivableManagerList: React.FC = () => {
  const searchParams: AccountsReceivableSearchCondition = require('query-string').parse(window.location.search)
  Object.keys(searchParams).forEach(k => {
    if (searchParams[k] === 'undefined') searchParams[k] = undefined
  })
  const { t } = useTranslation()
  const { setIsLoading } = useContext(LoaderContextCreator())
  const { errorHandler } = useErrorHandler()
  const history = useHistory()
  const { scrollRef: listScrollRef, scrollToTop: scrollListToTop } = useScrollToTop()

  const [isOpenReconciliationModal, setIsOpenReconciliationModal] = useState(false)
  const [isOpenFilterPopup, setIsOpenFilterPopup] = useState(false)
  const [openedAR, setOpenedAR] = useState<AccountsReceivable[]>([])
  const [checkedSales, setCheckedSales] = useState<FetchAccountsReceivableResponse['selectionItems']>([])
  const [searchResponse, setSearchResponse] = useState<FetchAccountsReceivableResponse>()
  const [searchCondition, setSearchCondition] = useState<AccountsReceivableSearchCondition>(
    searchParams?.page ? searchParams : defaultSearchCondition,
  )
  const [accountsReceivableMasters, setAccountsReceivableMasters] = useState<Array<{ id: string; name: string }>>([])

  const doSearch = async (searchCondition: AccountsReceivableSearchCondition) => {
    try {
      setIsLoading(true)
      setSearchResponse(await fetchAccountsReceivableForManager(searchCondition))
      history.replace({
        pathname: '',
        search: new URLSearchParams(searchCondition as any).toString(),
      })
      scrollListToTop()
    } catch (error) {
      errorHandler(error)
    } finally {
      setIsLoading(false)
    }
  }

  const refresh = (val: AccountsReceivableSearchCondition) => {
    setSearchCondition({ ...val, page: 1 })
    setIsOpenFilterPopup(false)
    setIsOpenReconciliationModal(false)
    setOpenedAR([])
    setCheckedSales([])
  }

  const doExport = async () => {
    try {
      setIsLoading(true)
      const { url } = await exportAccountsReceivableForManager(searchCondition)
      window.open(url, '_blank')
    } catch (error) {
      errorHandler(error)
    } finally {
      setIsLoading(false)
    }
  }

  const isDisabledDiffAR = (ar: AccountsReceivable): boolean => {
    if (checkedSales.length === 0) {
      return false
    }
    return checkedSales[0].receiptGroupKey !== ar.receiptGroupKey || checkedSales[0].isDiscount !== ar.isDiscount
  }

  const checkedSaleKeySet = useMemo(() => {
    return new Set(checkedSales.map(sale => `${sale.id}:${sale.isDiscount ? 1 : 0}`))
  }, [checkedSales])

  const isCheckedSale = (saleId: string, isDiscount: boolean): boolean => checkedSaleKeySet.has(`${saleId}:${isDiscount ? 1 : 0}`)
  const isCheckedAllSalesInAR = (ar: AccountsReceivable): boolean => {
    if (ar.sales.length === 0) {
      return false
    }
    return ar.sales.every(sale => isCheckedSale(sale.id, ar.isDiscount))
  }

  const isCheckedPartialSalesInAR = (ar: AccountsReceivable): boolean => {
    if (ar.sales.length === 0) {
      return false
    }
    const checkedCount = ar.sales.filter(sale => isCheckedSale(sale.id, ar.isDiscount)).length
    return checkedCount > 0 && checkedCount < ar.sales.length
  }

  const onCheckedAR = (ar: AccountsReceivable, value: boolean): void => {
    setCheckedSales(prev => {
      const filtered = prev.filter(checked => !ar.sales.some(sale => sale.id === checked.id))
      if (!value) {
        return filtered
      }
      if (filtered.length > 0 && (filtered[0].receiptGroupKey !== ar.receiptGroupKey || filtered[0].isDiscount !== ar.isDiscount)) {
        return filtered
      }
      const addTargets = ar.sales.map(sale => ({
        id: sale.id,
        receiptGroupKey: ar.receiptGroupKey,
        subjectPrice: sale.subjectPrice,
        isDiscount: ar.isDiscount,
      }))
      return [...filtered, ...addTargets]
    })
  }

  const onCheckedSale = (ar: AccountsReceivable, sale: AccountsReceivable['sales'][number], value: boolean): void => {
    setCheckedSales(prev => {
      const filtered = prev.filter(checked => checked.id !== sale.id)
      if (!value) {
        return filtered
      }
      if (filtered.length > 0 && (filtered[0].receiptGroupKey !== ar.receiptGroupKey || filtered[0].isDiscount !== ar.isDiscount)) {
        return filtered
      }
      return [...filtered, { id: sale.id, receiptGroupKey: ar.receiptGroupKey, subjectPrice: sale.subjectPrice, isDiscount: ar.isDiscount }]
    })
  }

  const isOpenedAR = (ar: AccountsReceivable): boolean => {
    return openedAR.findIndex(opened => opened.id === ar.id) !== -1
  }

  const onChangeSortKey = (sortKey: SortKeyType) => {
    if (searchCondition.sortKey === sortKey) {
      setSearchCondition({
        ...searchCondition,
        page: 1,
        sortOrder: searchCondition.sortOrder === SortOrderType.Asc ? SortOrderType.Desc : SortOrderType.Asc,
      })
    } else {
      setSearchCondition({ ...searchCondition, page: 1, sortOrder: SortOrderType.Asc, sortKey })
    }
  }

  const onCheckedAllItem = (): void => {
    if (!searchResponse) {
      return
    }
    setCheckedSales(isAllSelected ? [] : searchResponse.selectionItems)
  }

  useEffect(() => {
    setOpenedAR([])
    doSearch(searchCondition)
  }, [searchCondition])

  useEffect(() => {
    fetchSalesAccountsReceivable().then(res => {
      setAccountsReceivableMasters(res?.accountsReceivables || [])
    })
  }, [])

  const isAllSelected = useMemo((): boolean => {
    if (!searchResponse || searchResponse.selectionItems.length === 0 || checkedSales.length === 0) {
      return false
    }
    return searchResponse.selectionItems.every(item => isCheckedSale(item.id, checkedSales[0].isDiscount))
  }, [searchResponse, checkedSales])

  const selectedSaleTotalPrice = useMemo(() => checkedSales.reduce((prev, sale) => prev + sale.subjectPrice, 0), [checkedSales])
  const filterLabel = useMemo((): Array<string | undefined> => {
    if (accountsReceivableMasters.length === 0) {
      return []
    }
    return [
      searchCondition.checkinFrom
        ? `${t('Check-in date')}:${dayjs(searchCondition.checkinFrom).format('YYYY/M/D')}~${dayjs(searchCondition.checkinTo).format(
            'YYYY/M/D',
          )}`
        : undefined,
      searchCondition.checkoutFrom
        ? `${t('Check-out date')}:${dayjs(searchCondition.checkoutFrom).format('YYYY/M/D')}~${dayjs(searchCondition.checkoutTo).format(
            'YYYY/M/D',
          )}`
        : undefined,
      searchCondition.customerName ? `${t('Accounts receivable')}:${searchCondition.customerName}` : undefined,
      searchCondition.salesType ? `${t('Type')}:${t(searchCondition.salesType === 'sales' ? 'Sales' : 'Discount')}` : undefined,
      searchCondition.reconciledStatus
        ? `${t('Reconciliation status')}:${t(searchCondition.reconciledStatus === 'reconciled' ? 'Reconciled' : 'Unreconciled')}`
        : undefined,
    ].filter(label => label)
  }, [searchCondition, accountsReceivableMasters])

  return (
    <DashboardPageLayout title={t('A/R Management')}>
      <TabContainer currentTab="A/R List" contentRef={listScrollRef}>
        <div className="headline-block">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Popover
              placement="bottomRight"
              trigger="click"
              content={
                isOpenFilterPopup ? (
                  <AccountsReceivableFilterPopup
                    searchCondition={searchCondition}
                    accountsReceivableMasters={accountsReceivableMasters}
                    onFilter={refresh}
                  />
                ) : (
                  <></>
                )
              }
              visible={isOpenFilterPopup}
              onVisibleChange={setIsOpenFilterPopup}
            >
              <Button
                icon={
                  filterLabel.length
                    ? require('@/static/images/icon_filter_active.svg')
                    : require('@/static/images/icon_filter_disable.svg')
                }
                buttonType={filterLabel.length ? ButtonType.Simple : ButtonType.NonStyle}
                width={120}
                height={32}
                fontSize={12}
              >
                {t('Filter')}
              </Button>
            </Popover>
            <div style={{ fontSize: 12, lineHeight: 1.5, fontWeight: 'bold', color: '#676767', letterSpacing: 0.6 }}>
              {filterLabel.join('、')}
            </div>
          </div>
          <div>
            <Button
              buttonType={ButtonType.NonStyle}
              width={170}
              height={32}
              fontSize={12}
              icon={require('@/static/images/csv.svg')}
              onClick={doExport}
            >
              {t('Export CSV')}
            </Button>
          </div>
        </div>

        <div className="indicator-block">
          <div>
            <div className="sub-title">{t('A/R Balance')}</div>
            <div className="title">¥{searchResponse?.indicator.accountsReceivableBalance.toLocaleString() || '-'}</div>
          </div>
          <div>
            <div className="sub-title">{t('Total A/R')}</div>
            <div className="title">¥{searchResponse?.indicator.totalAccountsReceivable.toLocaleString() || '-'}</div>
          </div>
          <div>
            <div className="sub-title">{t('Total receivables')}</div>
            <div className="title">¥{searchResponse?.indicator.totalReceiptPrice.toLocaleString() || '-'}</div>
          </div>
        </div>

        <div className="table-description-block">
          {checkedSales.length ? (
            <div className="sub-title">{`${checkedSales.length}${t('detail selected')} (¥${selectedSaleTotalPrice.toLocaleString()})`}</div>
          ) : (
            <div></div>
          )}
          <div style={{ display: 'flex' }}>
            <Button
              buttonType={checkedSales.length === 0 ? ButtonType.PrimaryDisabled : ButtonType.Primary}
              width={100}
              height={32}
              isDisabled={checkedSales.length === 0}
              onClick={() => setIsOpenReconciliationModal(true)}
            >
              {t('Reconciliation')}
            </Button>
            <AccommodationPaging
              page={Number(searchCondition?.page || 1)}
              total={searchResponse?.total || 0}
              limit={PER_PAGE_NUMBER}
              setPage={page => setSearchCondition({ ...searchCondition, page })}
            />
          </div>
        </div>

        <div className="table-block">
          {searchResponse && searchResponse.total !== 0 ? (
            <div css={tableStyle}>
              <div className="table-header">
                <div className="table-row">
                  <div className="table-cell col-check-box">
                    <Checkbox
                      value={isAllSelected}
                      partial={searchResponse.isEnabledAllSelect && !isAllSelected && checkedSales.length > 0}
                      disabled={!searchResponse.isEnabledAllSelect}
                      onChange={onCheckedAllItem}
                    />
                    <Hint
                      customCss={css({ position: 'static', height: 14, '.help-popup': { marginTop: 4, padding: 16, lineHeight: 1.5 } })}
                    >
                      {t(
                        'Accounts receivable of the same type as the account to which they are receivable can be combined and processed for cancellation.',
                      )}
                      <br />
                      {t('Select All is useful when the filter is focused on only one account and only one type of account.')}
                    </Hint>
                  </div>
                  <div className="table-cell col-details"></div>
                  <div className="table-cell col-checkin-date sortable-col" onClick={() => onChangeSortKey(SortKeyType.CheckinDate)}>
                    {t('Check-in date')}
                    {searchCondition.sortKey === SortKeyType.CheckinDate ? (
                      <img
                        style={{ opacity: 1 }}
                        src={
                          searchCondition.sortOrder === SortOrderType.Asc
                            ? require('@/static/images/icon_arrow_up_2.svg')
                            : require('@/static/images/icon_arrow_down_2.svg')
                        }
                      />
                    ) : (
                      <img src={require('@/static/images/icon_arrow_up_2.svg')} />
                    )}
                  </div>
                  <div className="table-cell col-ar sortable-col" onClick={() => onChangeSortKey(SortKeyType.CustomerName)}>
                    {t('Accounts receivable')}
                    {searchCondition.sortKey === SortKeyType.CustomerName ? (
                      <img
                        style={{ opacity: 1 }}
                        src={
                          searchCondition.sortOrder === SortOrderType.Asc
                            ? require('@/static/images/icon_arrow_up_2.svg')
                            : require('@/static/images/icon_arrow_down_2.svg')
                        }
                      />
                    ) : (
                      <img src={require('@/static/images/icon_arrow_up_2.svg')} />
                    )}
                  </div>
                  <div className="table-cell col-type sortable-col" onClick={() => onChangeSortKey(SortKeyType.IsDiscount)}>
                    {t('Type')}
                    {searchCondition.sortKey === SortKeyType.IsDiscount ? (
                      <img
                        style={{ opacity: 1 }}
                        src={
                          searchCondition.sortOrder === SortOrderType.Asc
                            ? require('@/static/images/icon_arrow_up_2.svg')
                            : require('@/static/images/icon_arrow_down_2.svg')
                        }
                      />
                    ) : (
                      <img src={require('@/static/images/icon_arrow_up_2.svg')} />
                    )}
                  </div>
                  <div className="table-cell col-guest-name">{t('Guest name')}</div>
                  <div className="table-cell col-reservation-id">{t('Reservation ID')}</div>
                  <div className="table-cell col-total-ar sortable-col" onClick={() => onChangeSortKey(SortKeyType.TotalPrice)}>
                    {t('Total A/R')}
                    {searchCondition.sortKey === SortKeyType.TotalPrice ? (
                      <img
                        style={{ opacity: 1 }}
                        src={
                          searchCondition.sortOrder === SortOrderType.Asc
                            ? require('@/static/images/icon_arrow_up_2.svg')
                            : require('@/static/images/icon_arrow_down_2.svg')
                        }
                      />
                    ) : (
                      <img src={require('@/static/images/icon_arrow_up_2.svg')} />
                    )}
                  </div>
                  <div className="table-cell col-deposit-day">{t('Deposit day')}</div>
                  <div className="table-cell col-reconciliation-status">{t('Reconciliation status')}</div>
                </div>
              </div>
              <div className="table-body">
                {searchResponse?.results.map(responseRow => (
                  <React.Fragment key={responseRow.id}>
                    <div className="table-row">
                      <div className="table-cell col-check-box">
                        {!responseRow.isReconciled && (
                          <Checkbox
                            value={isCheckedAllSalesInAR(responseRow)}
                            partial={isCheckedPartialSalesInAR(responseRow)}
                            onChange={v => onCheckedAR(responseRow, v)}
                            disabled={isDisabledDiffAR(responseRow)}
                          />
                        )}
                      </div>
                      <div className="table-cell col-details">
                        <img
                          onClick={() => {
                            if (isOpenedAR(responseRow)) {
                              setOpenedAR(openedAR.filter(ar => ar.id !== responseRow.id))
                            } else {
                              setOpenedAR([...openedAR, responseRow])
                            }
                          }}
                          css={css({ transform: `rotate(${isOpenedAR(responseRow) ? 90 : 0}deg)` })}
                          src={require('@/static/images/arrow_right.svg')}
                        />
                      </div>
                      <div className="table-cell col-checkin-date">
                        {!responseRow.checkinAt
                          ? '-'
                          : dayjs(responseRow.checkinAt).format('MM月DD日') +
                            (responseRow.nights ? ` ${responseRow.nights}${t('Night')}` : '')}
                      </div>
                      <div className="table-cell col-ar">{responseRow.customer.name}</div>
                      <div className="table-cell col-type">
                        <TypeLabel isDiscount={responseRow.isDiscount} />
                      </div>
                      <div className="table-cell col-guest-name">
                        {responseRow.reservationId ? (
                          <a
                            style={{ color: '#f2a40b' }}
                            href={`/reservation/${responseRow.reservationUuid}?reservationId=${responseRow.reservationId}`}
                          >
                            {responseRow.guestName || '-'}
                          </a>
                        ) : (
                          responseRow.guestName || '-'
                        )}
                      </div>
                      <div className="table-cell col-reservation-id">{responseRow.reservationId || '-'}</div>
                      <div className="table-cell col-total-ar">¥{responseRow.totalPrice.toLocaleString()}</div>
                      <div className="table-cell col-deposit-day">
                        {responseRow.receiptAt.length
                          ? responseRow.receiptAt.map(r => <div key={uuid()}>{dayjs(r).format('YYYY/M/D')}</div>)
                          : '-'}
                      </div>
                      <div className="table-cell col-reconciliation-status">
                        {responseRow.isReconciled ? (
                          <div className="reconciled-label">{t('Reconciled')}</div>
                        ) : (
                          <div className="unreconciled-label">{t('Unreconciled')}</div>
                        )}
                      </div>
                    </div>
                    <div style={{ display: isOpenedAR(responseRow) ? 'block' : 'none' }} css={detailTableStyle}>
                      <div className="table-title">{t('Usage details')}</div>
                      <div className="sub-table-block">
                        <div className="table-header">
                          <div className="table-row">
                            <div className="table-cell col-detail-check"></div>
                            <div className="table-cell col-detail-date">{t('Date')}</div>
                            <div className="table-cell col-detail-subject">
                              {t('Subject')}/{t('Supplementary subjects')}
                            </div>
                            <div className="table-cell col-detail-price">{t('Unit price')}</div>
                            <div className="table-cell col-detail-quantity">{t('Quantity')}</div>
                            <div className="table-cell col-detail-price">{t('Amount of money')}</div>
                            <div className="table-cell col-detail-payment">{t('Method of payment')}</div>
                          </div>
                        </div>
                        <div className="table-body">
                          {responseRow.sales.map(sale => (
                            <div className="table-row" key={sale.id}>
                              <div className="table-cell col-detail-check">
                                {!responseRow.isReconciled && (
                                  <Checkbox
                                    value={isCheckedSale(sale.id, responseRow.isDiscount)}
                                    disabled={isDisabledDiffAR(responseRow)}
                                    onChange={v => onCheckedSale(responseRow, sale, v)}
                                  />
                                )}
                              </div>
                              <div className="table-cell col-detail-date">{dayjs(sale.salesDate).format('YYYY/M/D')}</div>
                              <div className="table-cell col-detail-subject">
                                {[sale.subjectName, sale.subSubjectName].filter(n => n).join('/')}
                              </div>
                              <div className="table-cell col-detail-price">¥{sale.unitPrice.toLocaleString()}</div>
                              <div className="table-cell col-detail-quantity">{sale.quantity.toLocaleString()}</div>
                              <div className="table-cell col-detail-price">¥{sale.subjectPrice.toLocaleString()}</div>
                              <div className="table-cell col-detail-payment">{sale.paymentMethod}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>
          ) : (
            <div className="empty-table">
              <h1>{t('No data available')}</h1>
            </div>
          )}
        </div>

        {isOpenReconciliationModal && (
          <ReceiptReconciliationModal
            targetSaleIds={checkedSales.filter(sale => !sale.isDiscount).map(sale => sale.id)}
            targetDiscountIds={checkedSales.filter(sale => sale.isDiscount).map(sale => sale.id)}
            totalPrice={selectedSaleTotalPrice}
            onCancel={() => setIsOpenReconciliationModal(false)}
            onSave={() => refresh(searchCondition)}
          />
        )}
      </TabContainer>
    </DashboardPageLayout>
  )
}

const detailTableStyle = css({
  background: 'rgb(245,245,245)',
  '.table-title': { padding: 16, paddingBottom: 0, color: '#676767', fontWeight: 'bold', fontSize: 12 },
  '.sub-table-block': {
    padding: 16,
    '.col-detail-check': { minWidth: 26, maxWidth: 26 },
    '.col-detail-date': { minWidth: 110, maxWidth: 110 },
    '.col-detail-subject': {
      minWidth: `calc((100% - 8px - ${26 + 110 + 100 + 60 + 100 + 120}px - (16px * 6)))`,
      maxWidth: `calc((100% - 8px - ${26 + 110 + 100 + 60 + 100 + 120}px - (16px * 6)))`,
    },
    '.col-detail-quantity': { minWidth: 60, maxWidth: 60, textAlign: 'right', justifyContent: 'right' },
    '.col-detail-price': { minWidth: 100, maxWidth: 100, textAlign: 'right', justifyContent: 'right' },
    '.col-detail-payment': { minWidth: 120, maxWidth: 120 },
  },
})

const tableStyle = css({
  '.table-header .table-cell': {
    display: 'flex',
    alignItems: 'center',
    '&.sortable-col': {
      cursor: 'pointer',
      '> img': {
        width: '1rem',
        marginLeft: 4,
        opacity: 0,
        filter: 'brightness(0) saturate(100%) invert(47%) sepia(0%) saturate(3541%) hue-rotate(185deg) brightness(86%) contrast(101%)',
      },
      ':hover': {
        '> img': { opacity: 0.5 },
      },
    },
  },
  '.col-check-box': { minWidth: 40, maxWidth: 40, display: 'flex', alignItems: 'center' },
  '.col-details': {
    height: 30,
    minWidth: 30,
    maxWidth: 30,
    img: {
      borderRadius: 50,
      cursor: 'pointer',
      ':hover': { background: '#F2F2F2' },
    },
  },
  '.col-checkin-date': { minWidth: 110, maxWidth: 110 },
  '.col-ar': {
    minWidth: `calc((100% - 8px - ${40 + 30 + 110 + 60 + 108 + 100 + 80 + 80}px - (16px * 10)) / 2)`,
    maxWidth: `calc((100% - 8px - ${40 + 30 + 110 + 60 + 108 + 100 + 80 + 80}px - (16px * 10)) / 2)`,
  },
  '.col-type': { minWidth: 60, maxWidth: 60 },
  '.col-guest-name': {
    minWidth: `calc((100% - 8px - ${40 + 30 + 110 + 60 + 108 + 100 + 80 + 80}px - (16px * 10)) / 2)`,
    maxWidth: `calc((100% - 8px - ${40 + 30 + 110 + 60 + 108 + 100 + 80 + 80}px - (16px * 10)) / 2)`,
  },
  '.col-reservation-id': { minWidth: 108, maxWidth: 108 },
  '.col-total-ar': { minWidth: 100, maxWidth: 100, textAlign: 'right', justifyContent: 'right' },
  '.col-deposit-day': { minWidth: 80, maxWidth: 80 },
  '.col-reconciliation-status': {
    minWidth: 80,
    maxWidth: 80,
    '> div': {
      borderRadius: 50,
      border: '1px solid rgb(226, 232, 240)',
      width: 'fit-content',
      padding: '4px 8px',
      '&.reconciled-label': { background: '#CCCCCC', color: '#FFFFFF' },
      '&.unreconciled-label': { background: 'rgb(254, 249, 195)', color: 'rgb(133, 77, 14)' },
    },
  },
})
